import React from 'react';

import { Button, Modal } from 'react-bootstrap';
import { useIntl } from 'react-intl';

import { useAppSelector } from 'hooks';
import { TaskStatus } from 'constants/enums';
import { Error, Loading } from 'components/Fetch';
import { useAxios } from 'configs/requests';
import { TasksList } from 'components/UI';

interface IBulkArchiveTasksModalProps {
  handleClose: (refresh?: boolean) => void;
  status: TaskStatus;
}

interface IBulkArchiveTasksPayload {
  status: number;
  tasks: ITask[];
}

const BulkUpdateTasksStatusModal = ({ handleClose, status }: IBulkArchiveTasksModalProps) => {
  const { formatMessage } = useIntl();
  const selectedTasks: ITask[] = useAppSelector((state) => state.clinicalLogger.tasks);

  const [{ loading: loadingUpdateTasks, error: errorUpdateTasks }, bulkUpdateTasks] =
    useAxios<IBulkArchiveTasksPayload>(
      {
        url: '/clinician-gateway/Tasks/status',
        method: 'PATCH'
      },
      { manual: true }
    );

  const getModalTitle = () => {
    switch (status) {
      case TaskStatus.ARCHIVED:
        return 'tasks.actions.bulkArchiveTasks.modalTitle';
      case TaskStatus.ON_HOLD:
        return 'tasks.actions.bulkOnHoldTasks.modalTitle';
      default:
        return 'tasks.actions.bulkResumeTasks.modalTitle';
    }
  };

  const getWarning = () => {
    switch (status) {
      case TaskStatus.ARCHIVED:
        return 'tasks.actions.bulkArchiveTasks.warning';
      case TaskStatus.ON_HOLD:
        return 'tasks.actions.bulkOnHoldTasks.warning';
      default:
        return 'tasks.actions.bulkResumeTasks.warning';
    }
  };

  const handleUpdate = async () => {
    const payload: IBulkArchiveTasksPayload = {
      status: parseInt(status),
      tasks: selectedTasks
    };
    await bulkUpdateTasks({ data: payload });
    handleClose(true);
  };

  return (
    <Modal
      show
      onHide={handleClose}
      backdrop="static"
      data-test="bulk-status-update-tasks-modal"
      size="xl"
    >
      <Modal.Header className="align-items-center">
        <Modal.Title>
          {formatMessage({
            id: getModalTitle()
          })}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {errorUpdateTasks && <Error retry={handleUpdate} />}
        <p>
          {formatMessage(
            {
              id: getWarning()
            },
            { tasksCount: selectedTasks.length }
          )}
        </p>
        <TasksList tasks={selectedTasks} truncate={true} />
        {loadingUpdateTasks && <Loading className="mt-4" />}
      </Modal.Body>
      <Modal.Footer>
        <Button
          data-test="bulk-status-update-tasks-cancel"
          variant="link"
          onClick={() => handleClose()}
        >
          {formatMessage({ id: 'Cancel' })}
        </Button>
        <Button
          data-test="bulk-status-update-tasks-submit"
          variant="primary"
          onClick={handleUpdate}
        >
          {formatMessage({ id: 'submitKey' })}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default BulkUpdateTasksStatusModal;
